// CoachingPlaybookCard — a compact "how to fix this call" panel that
// lives at the bottom of the call-detail page. It pulls the same
// callback-playbook backend used by the Revivable Deal Queue and
// composes it inline (no modal) so a manager can review the call and
// the recommended fix on one screen.
//
// The backend endpoint `/api/analytics/revival/{call_id}/playbook` is
// LLM-generated and cached, so opening this card after the first load
// is instant and cheap. Only enabled for graded calls with a
// `customer_phone` (needed to identify the caller in the LLM prompt).
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Sparkles, Loader2, RefreshCw, Copy, Clock, Database, Users, MessageSquare, Mail, Phone, StickyNote, Lightbulb, X, CheckCircle2, XCircle, Send, ExternalLink, Ban, HelpCircle, ShieldAlert, GraduationCap } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { PracticeModal } from '../../components/intelligence/PracticeModal';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../components/ui/sheet';
import { useAuth } from '../../contexts/AuthContext';

// SMS sending is gated by the per-user sms_access flag alone (agents
// stage unlocked Jun 2026 — mirrors backend _require_sms_send_access).
export const canSendSms = (user) => !!user && user.sms_access === true;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_TONE = {
  call:        'bg-sky-100 text-sky-800 border-sky-200',
  sms:         'bg-purple-100 text-purple-800 border-purple-200',
  email:       'bg-emerald-100 text-emerald-800 border-emerald-200',
  note:        'bg-amber-100 text-amber-800 border-amber-200',
  status:      'bg-indigo-100 text-indigo-800 border-indigo-200',
  appointment: 'bg-rose-100 text-rose-800 border-rose-200',
  other:       'bg-slate-100 text-slate-700 border-slate-200',
};
const KIND_ICON = { sms: MessageSquare, email: Mail, call: Phone, note: StickyNote };

/**
 * DISC × cold-days micro-tip. Pure client-side rule (no LLM cost) —
 * fires below the sources panel with a one-line "what to do about it"
 * cue like "S caller · 21d cold → warm reopener needed". The rep reads
 * it in one glance before they touch the checklist.
 */
const DiscMicroTip = ({ disc, gymsales, playbook }) => {
  const d = (disc || playbook?.disc || 'S').toUpperCase();
  const days = gymsales?.matched ? gymsales?.counters?.days_since_last_contact : null;
  const hasSmsHint = !!(playbook?.pre_call_sms || '').trim();

  // Coldness band → drives the emoji + verb. When we have no CRM match
  // we still show a DISC tip but skip the days annotation.
  let coldLabel = 'fresh';
  let coldTone  = 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (days == null) { coldLabel = 'no CRM history'; coldTone = 'bg-slate-50 text-slate-600 border-slate-200'; }
  else if (days >= 30) { coldLabel = `${days}d cold`; coldTone = 'bg-rose-50 text-rose-700 border-rose-200'; }
  else if (days >= 14) { coldLabel = `${days}d cooling`; coldTone = 'bg-amber-50 text-amber-800 border-amber-200'; }
  else if (days >= 7)  { coldLabel = `${days}d quiet`;   coldTone = 'bg-amber-50 text-amber-700 border-amber-200'; }
  else                 { coldLabel = `${days}d warm`;    coldTone = 'bg-emerald-50 text-emerald-700 border-emerald-200'; }

  // DISC × coldness → coaching cue. Tuned to the Green Brain model —
  // never generic sales language. Falls back to a plain DISC tip when
  // we don't have days.
  const cue = (() => {
    if (hasSmsHint) {
      // The LLM already flagged an SMS-first approach — highlight it.
      return 'SMS-first approach recommended (see below)';
    }
    if (days != null && days >= 30) {
      const m = {
        D: 'lead with a direct outcome pitch — no small talk',
        I: 'open warm and social, reference their community/goals from CRM',
        S: 'warm reopener needed — apologise for the gap, reassure, restate their goal',
        C: 'send data-backed reopener (numbers, next-step dates), no pressure',
      };
      return m[d] || m.S;
    }
    if (days != null && days >= 14) {
      const m = {
        D: 'go straight to the plan — one concrete next step',
        I: 'match energy, drop the "what excites you most" question',
        S: 'reassure first, mention consistency of touch (weekly check-ins)',
        C: 'lead with the info gap they mentioned — bring numbers',
      };
      return m[d] || m.S;
    }
    // Fresh / warm / no history
    const m = {
      D: 'concise, outcome-and-plan framing',
      I: 'warm & social — match their energy',
      S: 'patient & reassuring, spell out "here\u2019s what happens next"',
      C: 'structured & specific — offer dates and detail',
    };
    return m[d] || m.S;
  })();

  return (
    <div
      className="rounded-md border border-teal-200 bg-teal-50 px-3 py-2 flex items-center gap-2 text-xs"
      data-testid="disc-micro-tip"
    >
      <Lightbulb className="w-3.5 h-3.5 text-teal-700 flex-shrink-0" />
      <span className={`text-[10px] font-bold border rounded-full px-2 py-0.5 ${coldTone}`}>
        {d} · {coldLabel}
      </span>
      <span className="text-teal-800 font-medium">→</span>
      <span className="text-teal-900 leading-tight">{cue}</span>
    </div>
  );
};

/**
 * Send buttons for the pre-call SMS block. Left button: YABBR (always
 * available if the tenant has creds); right button: GymSales deep-link
 * (only enabled when we have a matched GymSales person_id so the URL
 * can target the right profile). Confirms before firing to prevent
 * a fat-finger send.
 */
export const PreCallSmsSendButtons = ({ callId, content, gymsales }) => {
  const { user } = useAuth();
  const [busy, setBusy] = useState(null); // 'yabbr' | 'gymsales_link' | null
  const [simulated, setSimulated] = useState(false);
  const [text, setText] = useState(content || '');
  useEffect(() => { setText(content || ''); }, [content]);

  // Tenant isolation: domains without GymSales creds get ZERO GymSales UI.
  // Legacy cached playbooks lack `available` — fall back to the reason code.
  const gymsalesAvailable = gymsales
    ? (gymsales.available ?? !['no_credentials', 'not_attempted'].includes(gymsales.reason))
    : false;
  const hasGymSalesMatch = gymsalesAvailable && !!gymsales?.matched;

  // Role + per-user flag gate — agents (and managers without the
  // Team Users "SMS access" toggle on) never see the send controls.
  if (!canSendSms(user)) return null;

  const sendVia = async (via) => {
    if (!text?.trim()) return;
    const isSim = simulated && via === 'yabbr';
    const label = via === 'yabbr'
      ? (isSim ? 'Test-fire (simulated, no cost) via Yabbit SMS?' : 'Send via Yabbit SMS now?')
      : 'Open in GymSales composer?';
    if (!window.confirm(`${label}\n\n${text}`)) return;
    setBusy(via);
    try {
      const r = await axios.post(
        `${API}/analytics/revival/${callId}/send-sms`,
        { via, content: text, simulated: isSim },
      );
      if (via === 'gymsales_link') {
        if (r.data?.deep_link) {
          window.open(r.data.deep_link, '_blank', 'noopener,noreferrer');
          toast.success('Opened GymSales composer in a new tab.');
        } else {
          toast.error('No deep link returned.');
        }
      } else {
        const gs = r.data?.gymsales_note;
        const prefix = isSim ? 'Test-fire OK — no SMS sent' : 'SMS sent via Yabbit SMS';
        toast.success(
          gs?.ok
            ? `${prefix} · note logged to GymSales.`
            : (isSim ? `${prefix}.` : `${prefix}. (GymSales note-back failed.)`),
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Send via ${via === 'yabbr' ? 'Yabbit SMS' : 'GymSales'} failed.`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-2 space-y-2" data-testid="playbook-pre-call-sms-actions">
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, 459))}
        rows={3}
        className="w-full text-xs border border-purple-200 rounded-md px-2.5 py-2 bg-white/80 focus:outline-none focus:ring-2 focus:ring-purple-400"
        placeholder="Edit the SMS before sending…"
        data-testid="sms-edit-box"
      />
      <div className="text-[10px] text-slate-400 text-right">{text.length}/459</div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => sendVia('yabbr')}
          disabled={busy != null}
          className={`text-xs font-semibold px-3 py-1.5 rounded-md ${
            simulated ? 'bg-amber-500 hover:bg-amber-400' : 'bg-indigo-600 hover:bg-indigo-500'
          } disabled:opacity-50 text-white inline-flex items-center gap-1.5`}
          data-testid="pre-call-sms-send-yabbr"
          title={simulated
            ? 'Test-fire (simulated=true) — full round-trip through Yabbit SMS with no cost & no real handset'
            : 'Send this SMS immediately via Yabbit SMS'}
        >
          {busy === 'yabbr' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          {simulated ? 'Test-fire via Yabbit SMS' : 'Send via Yabbit SMS'}
        </button>
        {gymsalesAvailable && (
          <button
            type="button"
            onClick={() => sendVia('gymsales_link')}
            disabled={busy != null || !hasGymSalesMatch}
            className="text-xs font-semibold px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white inline-flex items-center gap-1.5"
            data-testid="pre-call-sms-send-gymsales"
            title={hasGymSalesMatch
              ? 'Open GymSales composer with this message pre-filled'
              : 'Disabled — no GymSales match on this caller. Use Yabbit SMS path.'}
          >
            {busy === 'gymsales_link' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5" />}
            Open in GymSales
          </button>
        )}
        <label
          className="ml-auto inline-flex items-center gap-1.5 text-[11px] text-purple-700 cursor-pointer select-none"
          data-testid="pre-call-sms-simulated-toggle"
          title="When on, the Yabbit SMS call goes through with simulated=true — no SMS actually sent, no cost. Perfect for QA."
        >
          <input
            type="checkbox"
            checked={simulated}
            onChange={(e) => setSimulated(e.target.checked)}
            className="rounded"
          />
          Test-fire mode (no SMS actually sent)
        </label>
      </div>
    </div>
  );
};


const TimelineDetailSheet = ({ open, onOpenChange, entry }) => {
  if (!entry) return null;
  const Icon = KIND_ICON[entry.kind] || StickyNote;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="timeline-detail-sheet">
        <SheetHeader className="pb-4 border-b border-slate-100">
          <SheetTitle className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Icon className="w-5 h-5 text-slate-500" />
            {entry.kind === 'sms' ? 'SMS' :
             entry.kind === 'email' ? 'Email' :
             entry.kind === 'call' ? 'Call' :
             entry.kind === 'note' ? 'Note' :
             entry.kind === 'appointment' ? 'Appointment' :
             entry.kind === 'status' ? 'Status Change' : 'Event'}
          </SheetTitle>
          <SheetDescription className="text-xs text-slate-500 tabular-nums">
            {entry.occurred_at || 'no date'}
            {entry.by ? ` · by ${entry.by}` : ''}
          </SheetDescription>
        </SheetHeader>
        <div className="pt-4 space-y-4">
          {entry.subject && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Subject</p>
              <p className="text-sm text-slate-800 font-medium">{entry.subject}</p>
            </div>
          )}
          {entry.body_excerpt && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Content</p>
              <div className="text-sm text-slate-800 bg-slate-50 border border-slate-200 rounded-lg p-3 whitespace-pre-wrap">
                {entry.body_excerpt}
              </div>
            </div>
          )}
          {entry.outcome && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Outcome</p>
              <span className="text-xs bg-slate-100 text-slate-800 px-2 py-1 rounded">{entry.outcome}</span>
            </div>
          )}
          {(entry.confirmed != null || entry.complete != null) && (
            <div className="flex gap-2">
              {entry.confirmed != null && (
                <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded ${
                  entry.confirmed ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                }`}>
                  {entry.confirmed ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                  {entry.confirmed ? 'confirmed' : 'unconfirmed'}
                </span>
              )}
              {entry.complete != null && (
                <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded ${
                  entry.complete ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                }`}>
                  {entry.complete ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                  {entry.complete ? 'complete' : 'open'}
                </span>
              )}
            </div>
          )}
          <p className="text-[10px] italic text-slate-400 pt-2 border-t border-slate-100">
            Full detail lives in GymSales. This is the summary the coaching plan was built from.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
};

/**
 * Compact "what data fed this plan" summary that renders above the
 * checklist. Shows whether GymSales was matched, key counters (visits,
 * SMS, emails, notes), the caller's journey stage, days since last
 * contact, and any objections we've seen from CRM notes. This is what
 * makes the rep trust the coaching plan — they can see the LLM had
 * real history to work with, not just the coached call transcript.
 */
const PlaybookSourcesPanel = ({ sources, disc, coachingModel, onTimelineClick }) => {
  if (!sources) return null;
  const gs = sources.gymsales || {};
  const followUps = sources.follow_up_calls_since || 0;
  const discTone = {
    D: 'bg-rose-100 text-rose-700 border-rose-200',
    I: 'bg-amber-100 text-amber-700 border-amber-200',
    S: 'bg-teal-100 text-teal-700 border-teal-200',
    C: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  }[disc] || 'bg-slate-100 text-slate-700 border-slate-200';

  return (
    <div className="rounded-lg border border-slate-200 bg-white/70 p-3" data-testid="playbook-sources-panel">
      <div className="flex items-center gap-2 mb-2">
        <Database className="w-3.5 h-3.5 text-slate-500" />
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
          Context feeding this plan
        </p>
        <span className={`ml-auto text-[10px] font-semibold border rounded-full px-2 py-0.5 ${discTone}`}>
          DISC {disc || '?'}
        </span>
        {coachingModel && (
          <span className="text-[10px] font-semibold border rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-700 border-emerald-200">
            {coachingModel === 'green_brain' ? 'Green Brain' : 'Legacy DISC'}
          </span>
        )}
      </div>

      {/* GymSales tile */}
      <div
        className={`rounded border p-2 ${
          gs.matched ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'
        }`}
        data-testid="playbook-source-gymsales"
      >
        <div className="flex items-center gap-2 mb-1">
          <Users className={`w-3.5 h-3.5 ${gs.matched ? 'text-emerald-600' : 'text-slate-400'}`} />
          <span className="text-xs font-bold text-slate-800">GymSales CRM</span>
          {gs.matched ? (
            <span
              className="text-[10px] font-semibold bg-emerald-200 text-emerald-800 px-1.5 py-0.5 rounded"
              data-testid="gymsales-matched-badge"
              title={gs.matched_at ? `Matched ${new Date(gs.matched_at).toLocaleString()}` : undefined}
            >
              matched{gs.matched_at ? ` · ${new Date(gs.matched_at).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}` : ''}
            </span>
          ) : (
            <span className="text-[10px] font-semibold bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">
              no match{gs.reason ? ` · ${gs.reason.replace(/_/g, ' ')}` : ''}
            </span>
          )}
        </div>

        {gs.matched && (
          <>
            <div className="text-xs text-slate-700 mb-1.5">
              <span className="font-semibold">{gs.person?.display_name || '(no name)'}</span>
              {gs.person?.journey_stage && (
                <span className="ml-2 text-slate-500">
                  · stage: <span className="font-mono">{gs.person.journey_stage}</span>
                </span>
              )}
              {gs.person?.salesperson && (
                <span className="ml-2 text-slate-500">· owner: {gs.person.salesperson}</span>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 text-[11px]">
              <SourceCounter icon={Phone}       label="Calls"    value={gs.counters?.calls_total} testid="gs-count-calls" />
              <SourceCounter icon={MessageSquare} label="SMS"    value={gs.counters?.sms_total} testid="gs-count-sms" />
              <SourceCounter icon={Mail}        label="Emails"   value={gs.counters?.emails_total} testid="gs-count-emails" />
              <SourceCounter icon={StickyNote}  label="Notes"    value={gs.counters?.notes_total} testid="gs-count-notes" />
            </div>

            {gs.counters?.days_since_last_contact != null && (
              <p className="text-[11px] text-slate-500 mt-1.5">
                Last touch: <span className="font-semibold text-slate-700">{gs.counters.days_since_last_contact}d ago</span>
                {(gs.counters?.appointments_total ?? 0) > 0 && (
                  <span className="ml-2">· {gs.counters.appointments_total} appt(s) logged</span>
                )}
              </p>
            )}

            {gs.objections_seen?.length > 0 && (
              <p className="text-[11px] mt-1.5">
                <span className="text-slate-500">Objections seen in CRM: </span>
                {gs.objections_seen.slice(0, 4).map((o, i) => (
                  <span key={i} className="ml-1 text-[10px] bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded">
                    {o}
                  </span>
                ))}
              </p>
            )}

            {gs.timeline_top?.length > 0 && (
              <details className="mt-1.5" open>
                <summary className="text-[11px] text-slate-500 hover:text-slate-800 cursor-pointer font-semibold">
                  Recent timeline ({gs.timeline_top.length}) — click any pill for detail
                </summary>
                <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="gs-timeline-pills">
                  {gs.timeline_top.map((e) => {
                    const tone = KIND_TONE[e.kind] || KIND_TONE.other;
                    const Icon = KIND_ICON[e.kind] || StickyNote;
                    const dateShort = (e.occurred_at || '').slice(0, 10);
                    const label = e.subject || e.outcome || (e.body_excerpt || '').slice(0, 40) || e.kind;
                    return (
                      <button
                        type="button"
                        key={e.id || (e.occurred_at + e.kind)}
                        onClick={() => onTimelineClick && onTimelineClick(e)}
                        className={`inline-flex items-center gap-1 text-[10px] font-semibold border rounded-full px-2 py-1 hover:brightness-95 transition ${tone}`}
                        data-testid={`gs-timeline-pill-${e.id}`}
                        title={e.body_excerpt || label}
                      >
                        <Icon className="w-2.5 h-2.5" />
                        <span className="font-mono opacity-75">{dateShort}</span>
                        <span className="uppercase">{e.kind}</span>
                        <span className="normal-case font-medium max-w-[160px] truncate">{label}</span>
                      </button>
                    );
                  })}
                </div>
              </details>
            )}
          </>
        )}
      </div>

      {followUps > 0 && (
        <p className="text-[11px] text-slate-600 mt-2 italic">
          Plus <span className="font-semibold text-slate-800">{followUps}</span> follow-up call{followUps === 1 ? '' : 's'} since the coached call — merged into the plan.
        </p>
      )}
    </div>
  );
};

const SourceCounter = ({ icon: Icon, label, value, testid }) => (
  <div className="flex items-center gap-1 bg-white/80 border border-slate-200 rounded px-2 py-1" data-testid={testid}>
    <Icon className="w-3 h-3 text-slate-500 flex-shrink-0" />
    <div className="min-w-0">
      <p className="text-[9px] uppercase text-slate-400 tracking-wider leading-none">{label}</p>
      <p className="text-sm font-bold tabular-nums text-slate-800 leading-tight">{value ?? 0}</p>
    </div>
  </div>
);

const CoachingPlaybookCard = ({ callId, call, grading }) => {
  const [playbook, setPlaybook] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [timelineDetail, setTimelineDetail] = useState(null);
  const [practiceOpen, setPracticeOpen] = useState(false);
  const cardRef = React.useRef(null);

  const canGenerate = !!(callId && call?.customer_phone && grading?.overall_score != null);

  const generate = async (refresh = false) => {
    if (!canGenerate) return;
    if (refresh) setRefreshing(true); else setLoading(true);
    try {
      const r = await axios.post(
        `${API}/analytics/revival/${callId}/playbook${refresh ? '?refresh=true' : ''}`,
      );
      setPlaybook(r.data);
      if (r.data?.auto_refreshed === 'new_crm_activity') {
        toast.success('Playbook rebuilt — new GymSales activity was found for this caller');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Playbook generation failed');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    // Do NOT auto-generate on mount — LLM budget concern. Wait for user click.
    setPlaybook(null);
  }, [callId]);

  // Top-of-page "Open Playbook" button (CallHeader) dispatches this event:
  // scroll the card into view and kick off generation if not started yet.
  useEffect(() => {
    const onOpen = () => {
      cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (!playbook && !loading) generate(false);
    };
    window.addEventListener('vf-open-playbook', onOpen);
    return () => window.removeEventListener('vf-open-playbook', onOpen);
  });

  const copyText = () => {
    if (!playbook) return;
    const lines = [];
    if (playbook.opening_line) lines.push(`Opening: ${playbook.opening_line}`);
    if (playbook.checklist?.length) {
      lines.push('\nChecklist:');
      playbook.checklist.forEach((s, i) => lines.push(`${i + 1}. ${s}`));
    }
    if (playbook.discovery_gaps?.length) {
      lines.push('\nDiscovery gaps (go deeper):');
      playbook.discovery_gaps.forEach((g) => {
        lines.push(`• ${g.gap}`);
        if (g.question) lines.push(`  Ask: "${g.question}"`);
        if (g.listen_for) lines.push(`  Listen for: ${g.listen_for}`);
      });
    }
    if (playbook.objection_handling?.length) {
      lines.push('\nObjection handling:');
      playbook.objection_handling.forEach((o) => {
        lines.push(`• ${o.objection || 'Objection'}: "${o.script || ''}"`);
        if (o.follow_up) lines.push(`  If they hold: "${o.follow_up}"`);
      });
    }
    if (playbook.points_to_cover?.length) {
      lines.push('\nPoints to cover:');
      playbook.points_to_cover.forEach((p) => lines.push(`• ${p}`));
    }
    if (playbook.urgency_note) lines.push(`\nUrgency: ${playbook.urgency_note}`);
    navigator.clipboard.writeText(lines.join('\n'));
    toast.success('Playbook copied to clipboard');
  };

  if (!canGenerate) return null;

  return (
    <Card ref={cardRef} className="border-teal-200 bg-gradient-to-br from-teal-50/60 to-white scroll-mt-4" data-testid="coaching-playbook-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-teal-600" /> Callback playbook
          <span className="text-xs text-slate-400 font-normal ml-1">
            · DISC-tuned opening line + step checklist + points to cover
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!playbook && !loading && (
          <div className="text-center py-4">
            <button
              onClick={() => generate(false)}
              className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-lg px-4 py-2"
              data-testid="coaching-playbook-generate-btn"
            >
              <Sparkles className="w-4 h-4" /> Generate playbook
            </button>
            <p className="text-xs text-slate-500 mt-2 italic">Uses the caller&apos;s DISC style + the call&apos;s missed opportunities to compose a callback script.</p>
          </div>
        )}
        {loading && (
          <div className="flex flex-col items-center gap-2 py-6" data-testid="playbook-generating-message">
            <Loader2 className="w-6 h-6 text-teal-500 animate-spin" />
            <p className="text-sm text-slate-600 font-medium">A new playbook is being generated with the latest data from GymSales…</p>
            <p className="text-xs text-slate-400">Pulling the caller&apos;s notes, calls, emails and SMS history — usually under 10 seconds.</p>
          </div>
        )}
        {playbook && playbook.do_not_pursue && (
          <div
            className="rounded-lg border border-rose-300 bg-rose-50 p-4"
            data-testid="playbook-do-not-pursue"
          >
            <div className="flex items-center gap-2 flex-wrap">
              <Ban className="w-4 h-4 text-rose-700" />
              <span className="text-sm font-bold text-rose-900">
                Expected {playbook.gymsales_flag === 'lost' ? 'lost deal' : 'bad lead'} — GymSales
              </span>
              {(playbook.flag_tags || []).map((t) => (
                <span key={t} className="text-[10px] font-bold uppercase bg-rose-200 text-rose-800 px-2 py-0.5 rounded-full">
                  {t}
                </span>
              ))}
            </div>
            <p className="text-sm text-rose-800 mt-2">
              {playbook.message}
              {playbook.person_name ? ` (GymSales contact: ${playbook.person_name})` : ''}
            </p>
            <p className="text-[11px] italic text-rose-600 mt-1.5">
              If the tag was removed in GymSales, press Generate again to re-check.
            </p>
          </div>
        )}
        {playbook && !playbook.do_not_pursue && (
          <div className="space-y-3">
            <PlaybookSourcesPanel
              sources={playbook.sources}
              disc={playbook.disc}
              coachingModel={playbook.coaching_model}
              onTimelineClick={(entry) => setTimelineDetail(entry)}
            />
            <DiscMicroTip disc={playbook.disc} gymsales={playbook.sources?.gymsales} playbook={playbook} />
            {playbook.pre_call_sms && (
              <div
                className="rounded-lg border border-purple-200 bg-purple-50 p-3"
                data-testid="playbook-pre-call-sms"
              >
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <MessageSquare className="w-3.5 h-3.5 text-purple-700" />
                  <p className="text-[11px] font-bold uppercase tracking-wider text-purple-800">
                    Suggested SMS before / instead of call
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(playbook.pre_call_sms).then(
                        () => toast.success('SMS copied'),
                        () => toast.error('Copy failed'),
                      );
                    }}
                    className="ml-auto text-[10px] text-purple-700 hover:text-purple-900 inline-flex items-center gap-1"
                    data-testid="playbook-pre-call-sms-copy"
                  >
                    <Copy className="w-3 h-3" /> Copy
                  </button>
                </div>
                <p className="text-sm text-purple-900 leading-snug bg-white/70 rounded p-2 border border-purple-100">
                  {playbook.pre_call_sms}
                </p>
                {playbook.pre_call_sms_reason && (
                  <p className="text-[11px] italic text-purple-700 mt-1.5">
                    Why: {playbook.pre_call_sms_reason}
                  </p>
                )}
                <PreCallSmsSendButtons
                  callId={callId}
                  content={playbook.pre_call_sms}
                  gymsales={playbook.sources?.gymsales}
                />
              </div>
            )}
            {playbook.opening_line && (
              <blockquote className="border-l-4 border-teal-500 pl-3 py-1 bg-white/60 italic text-sm text-slate-800" data-testid="playbook-opening-line">
                “{playbook.opening_line}”
              </blockquote>
            )}
            {playbook.checklist?.length > 0 && (
              <div>
                <p className="text-[11px] uppercase text-slate-500 font-semibold mb-1">Checklist</p>
                <ol className="list-decimal list-inside space-y-1 text-sm text-slate-800">
                  {playbook.checklist.map((s, i) => <li key={`c-${i}`}>{s}</li>)}
                </ol>
              </div>
            )}
            {playbook.discovery_gaps?.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="playbook-discovery-gaps">
                <p className="text-[11px] uppercase tracking-wider text-amber-800 font-bold mb-2 flex items-center gap-1.5">
                  <HelpCircle className="w-3.5 h-3.5" /> Green Brain discovery gaps — go deeper this time
                </p>
                <div className="space-y-2">
                  {playbook.discovery_gaps.map((g, i) => (
                    <div key={`dg-${i}`} className="bg-white/70 border border-amber-100 rounded p-2">
                      <p className="text-xs font-semibold text-amber-900">{g.gap}</p>
                      {g.question && (
                        <p className="text-sm text-slate-800 mt-1">
                          <span className="text-[10px] font-bold uppercase text-amber-700 mr-1">Ask</span>
                          <span className="italic">“{g.question}”</span>
                        </p>
                      )}
                      {g.listen_for && (
                        <p className="text-[11px] text-amber-700 mt-1">
                          <span className="font-semibold">Listen for:</span> {g.listen_for}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {playbook.objection_handling?.length > 0 && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 p-3" data-testid="playbook-objection-handling">
                <p className="text-[11px] uppercase tracking-wider text-rose-800 font-bold mb-2 flex items-center gap-1.5">
                  <ShieldAlert className="w-3.5 h-3.5" /> Objection handling — exact words
                </p>
                <div className="space-y-2">
                  {playbook.objection_handling.map((o, i) => (
                    <div key={`oh-${i}`} className="bg-white/70 border border-rose-100 rounded p-2">
                      {o.objection && (
                        <span className="text-[10px] font-bold uppercase bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full">
                          {o.objection}
                        </span>
                      )}
                      {o.script && (
                        <p className="text-sm text-slate-800 italic mt-1.5">“{o.script}”</p>
                      )}
                      {o.follow_up && (
                        <p className="text-[11px] text-rose-700 mt-1">
                          <span className="font-semibold">If they hold:</span> “{o.follow_up}”
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {playbook.points_to_cover?.length > 0 && (
              <div>
                <p className="text-[11px] uppercase text-slate-500 font-semibold mb-1">Points to cover</p>
                <ul className="list-disc list-inside space-y-0.5 text-sm text-slate-800">
                  {playbook.points_to_cover.map((p, i) => <li key={`p-${i}`}>{p}</li>)}
                </ul>
              </div>
            )}
            <div className="flex items-center gap-3 pt-2 flex-wrap">
              {playbook.close_probability != null && (
                <span className="text-xs bg-indigo-100 text-indigo-800 border border-indigo-200 rounded-full px-2.5 py-0.5 font-semibold">
                  {playbook.close_probability}% close probability
                </span>
              )}
              {playbook.urgency_note && (
                <span className="text-xs inline-flex items-center gap-1 bg-amber-100 text-amber-900 border border-amber-200 rounded-full px-2.5 py-0.5">
                  <Clock className="w-3 h-3" />{playbook.urgency_note}
                </span>
              )}
              <div className="ml-auto flex items-center gap-2">
                <RouterLink
                  to="/training"
                  className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900 border border-emerald-300 bg-emerald-50 rounded-lg px-2.5 py-1.5 font-semibold"
                  data-testid="playbook-review-training-link"
                  title="Open the Training hub — the playbooks and knowledge behind this coaching"
                >
                  <GraduationCap className="w-3 h-3" /> Review training
                </RouterLink>
                <button
                  onClick={() => setPracticeOpen(true)}
                  className="inline-flex items-center gap-1 text-xs text-indigo-700 hover:text-indigo-900 border border-indigo-300 bg-indigo-50 rounded-lg px-2.5 py-1.5 font-semibold"
                  data-testid="playbook-practice-btn"
                  title="Rehearse this callback against an AI playing the customer"
                >
                  <Sparkles className="w-3 h-3" /> Practice
                </button>
                <button
                  onClick={copyText}
                  className="inline-flex items-center gap-1 text-xs text-slate-700 hover:text-slate-900 border border-slate-300 rounded-lg px-2.5 py-1.5"
                  data-testid="playbook-copy-btn"
                >
                  <Copy className="w-3 h-3" /> Copy
                </button>
                <button
                  onClick={() => generate(true)}
                  disabled={refreshing}
                  className="inline-flex items-center gap-1 text-xs text-teal-700 hover:text-teal-900 border border-teal-300 rounded-lg px-2.5 py-1.5 disabled:opacity-50"
                  data-testid="playbook-refresh-btn"
                >
                  {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                  Refresh
                </button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
      <TimelineDetailSheet
        open={!!timelineDetail}
        onOpenChange={(v) => { if (!v) setTimelineDetail(null); }}
        entry={timelineDetail}
      />
      {practiceOpen && (
        <PracticeModal callId={callId} openingLine={playbook?.opening_line} onClose={() => setPracticeOpen(false)} />
      )}
    </Card>
  );
};

export default CoachingPlaybookCard;
